/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AsyncSelect from "react-select/async";

import * as url from "helper/UrlHelper";
import { getData, putData, postData } from "utils/Gateway";
import { reactSelectStyle } from "Config/Config"; // Using reactSelectStyle instead of customSelectStyle
import {
  convertTo24Hour,
  getMatchingDates,
  to12HourFormat,
  weekDaysList,
} from "helper/DateTimeHelper/DateTimeHelper";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper"; // Re-using getMemberOptions

const SaveMedicationPopup = ({
  afterActionClose = () => {},
  selectedMedicationId = null, // Renamed from medicationId to selectedMedicationId
  setSelectedMedicationId = () => {}, // Renamed from setMedicationId to setSelectedMedicationId
}) => {
  const todayValue = new Date().toISOString().split("T")[0];
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const userObj = {
    label: `${userInfo.name ?? userInfo.email} ${userInfo.surname ?? ""}`,
    value: userInfo._id,
  };

  const medicineUsageTemplate = {
    dose: "",
    time: "",
    direction: "",
    additionaldirection: "",
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [medicine, setMedicine] = useState(null);
  const [medicineUsage, setMedicineUsage] = useState([medicineUsageTemplate]);
  const [usageDaysOfweek, setUsageDaysOfweek] = useState([]);
  const [startDate, setStartDate] = useState(todayValue);
  const [endDate, setEndDate] = useState(todayValue);
  const [usageDates, setUsageDates] = useState([]);
  const [comments, setComments] = useState("");
  const [assignTo, setAssignTo] = useState(null);
  const [referredBy, setReferredBy] = useState(null); // Added referredBy state

  const [isSaving, setIsSaving] = useState(false);

  // async load options for medicines
  const loadMedicinesOptions = async (inputValue) => {
    try {
      let requestUrl = url.SERVER_URL + url.MEDICINES_OPTIONS;

      const response = await getData(requestUrl);

      return response.filter((item) =>
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      );
    } catch (error) {
      console.log(error.message);
      return [];
    }
  };

  // Async load options for assignee and referredBy
  const loadUserOptions = async (inputValue) => {
    try {
      const options = await getMemberOptions(inputValue);
      // Check if current user already exists in API results
      const userExists = options.some((item) => item.value === userObj.value);
      // Append current user ONLY if not found in API response
      return userExists ? options : [...options, userObj];
    } catch (error) {
      return [];
    }
  };

  //function for add new medicine usage
  const addNewMedicineUsage = () => {
    setMedicineUsage([...medicineUsage, { ...medicineUsageTemplate }]);
  };

  //function for delete medicine usage
  const deleteMedicineUsage = (index) => {
    setMedicineUsage((prevUsage) => prevUsage.filter((_, i) => i !== index));
  };

  //function for handle medicine usage change
  const handleMedicineUsageChange = (index, field, value) => {
    setMedicineUsage((prev) =>
      prev.map((usage, i) =>
        i === index ? { ...usage, [field]: value } : usage
      )
    );
  };

  //function for map medicine usage from api
  const mapMedicineUsageFromApi = (apiData = []) => {
    if (apiData.length === 0) return [];

    const mappedData = apiData.map((item) => {
      return {
        dose: item.dose,
        time: convertTo24Hour(item.time),
        direction: item.direction,
        additionaldirection: item.additionaldirection,
      };
    });

    setMedicineUsage(mappedData);
  };

  // Handler for checkbox
  const handleDayToggle = (day) => {
    const dayValue = day.value;
    setUsageDaysOfweek((prev) =>
      prev.includes(dayValue)
        ? prev.filter((v) => v !== dayValue)
        : [...prev, dayValue]
    );
  };

  //function for get medication details
  const getMedicationDetails = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_MEDICINE_BY_ID +
        `/${selectedMedicationId}?token=${token}`;

      console.log("requestUrl", requestUrl);

      const response = await getData(requestUrl);

      if (response.status) {
        const medicationData = response.data;

        setMedicine(medicationData?.medicinevalue ?? null);
        mapMedicineUsageFromApi(medicationData?.usagelist ?? []);

        setUsageDaysOfweek(medicationData?.usagedaysofweek ?? []);
        setStartDate(
          medicationData?.startdate
            ? new Date(medicationData.startdate).toISOString().split("T")[0]
            : ""
        );
        setEndDate(
          medicationData?.enddate
            ? new Date(medicationData.enddate).toISOString().split("T")[0]
            : ""
        );
        setUsageDaysOfweek(medicationData.usagedaysofweek ?? []);

        setComments(medicationData.comments);

        if (medicationData?.assignedtovalue) {
          setAssignTo({
            value: medicationData.assignedtovalue,
            label: medicationData.assignedtolabel,
          });
        }
        if (medicationData?.referredbyvalue) {
          setReferredBy({
            value: medicationData.referredbyvalue,
            label: medicationData.referredbylabel,
          });
        }
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  //function for save
  const saveMedicineHandler = async () => {
    try {
      setIsSaving(true);

      // format dates
      const startDateFormatted = new Date(startDate).toISOString();
      const endDateFormatted = new Date(endDate).toISOString();

      const apiData = {
        prescribedby: userInfo._id,
        referredby: referredBy ? referredBy.value : userInfo._id, // Use referredBy state
        name: medicine?.name,
        duration: usageDates.length,
        startdate: startDateFormatted,
        enddate: endDateFormatted,
        medicineusages: medicineUsage.map((usage) => ({
          ...usage,
          time: to12HourFormat(usage.time),
        })),
        usagedaysofweek: usageDaysOfweek,
        usagedates: usageDates,
        comments,
        assignto: assignTo ? assignTo.value : null,
      };

      console.log("apiData", apiData);

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedMedicationId) {
        requestUrl +=
          url.API_UPDATE_MEDICINE + `/${selectedMedicationId}?token=${token}`; // Assuming selectedMedicationId is the ID of the medication to update

        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_ADD_MEDICINE + `?token=${token}`;

        response = await postData(requestUrl, apiData);
      }

      if (response.status) {
        //close offcanvas
        bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveMedicationOffCanvas")
        ).hide();
        resetHandler();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  //function for reset all states
  const resetHandler = () => {
    setCurrentStep(1);
    setMedicine(null);
    setMedicineUsage([medicineUsageTemplate]);
    setUsageDaysOfweek([]);
    setStartDate(todayValue);
    setEndDate(todayValue);
    setUsageDates([]);
    setComments("");
    setAssignTo(null);
    setReferredBy(null); // Reset referredBy
    setSelectedMedicationId(null);
  };

  useEffect(() => {
    if (selectedMedicationId) {
      getMedicationDetails();
    }
  }, [selectedMedicationId]);

  useEffect(() => {
    const usageMatchingDates = getMatchingDates(
      startDate,
      endDate,
      usageDaysOfweek
    );

    setUsageDates(usageMatchingDates);
  }, [startDate, endDate, usageDaysOfweek]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveMedicationOffCanvas"
      aria-labelledby="saveMedicationOffCanvasLabel"
    >
      {/* Header */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary icon-md">
              medical_information
            </span>
          </div>
          <div>
            <p
              className="offcanvas-title text-white mb-0"
              id="saveMedicationOffCanvasLabel"
            >
              <span className="d-block pe-3 me-3">
                <span className="fw-bold">{currentStep}</span> of 3
              </span>
              {currentStep === 1
                ? "Search for the Medicine"
                : currentStep === 2
                ? "Describe the amount of medicine patient take"
                : "Schedule of taking Medicine"}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div
        className="progress"
        role="progressbar"
        aria-label="Example 1px high"
        aria-valuenow={
          currentStep === 1 ? "33.333" : currentStep === 2 ? "66.666" : "100"
        }
        aria-valuemin="0"
        aria-valuemax="100"
        style={{ height: "2px" }}
      >
        <div
          className="progress-bar"
          style={{
            width:
              currentStep === 1
                ? "33.333%"
                : currentStep === 2
                ? "66.666%"
                : "100%",
          }}
        ></div>
      </div>
      <div className="offcanvas-body p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-white">
          <div className={currentStep === 1 ? "d-block" : "d-none"} id="step_1">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="form-group mb-4">
                <label className="d-block fw-semibold mb-2">
                  Name of the Medicine{" "}
                  <span className="text-danger fw-bold">*</span>
                </label>
                <AsyncSelect
                  isClearable
                  name="medicineName"
                  loadOptions={loadMedicinesOptions}
                  defaultOptions={false}
                  cacheOptions={false}
                  placeholder="Type medicine name"
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  value={medicine}
                  onChange={setMedicine}
                />
              </div>
            </form>

            <div className={medicine ? "medicine" : "d-none"}>
              <div className="d-flex gap-3">
                <div
                  className="img border d-flex align-items-center justify-content-center p-2 rounded-8 overflow-hidden"
                  style={{ width: "60px", height: "60px" }}
                >
                  <span className="material-symbols-outlined icon-xl text-primary">
                    health_and_safety
                  </span>
                </div>
                <div>
                  <p className="mb-0">{medicine?.name}</p>
                  <p className="font-sm text-gray mb-1">{medicine?.generic}</p>
                </div>
              </div>
              <div className="benefit pt-3 border-top border-gray-100 mt-3">
                <p className="fw-semibold mb-2">Benefits</p>
                <p className="font-sm text-gray">{medicine?.benifits}</p>
              </div>
              <div className="side-effects pt-3 border-top border-gray-100 mt-3">
                <p className="fw-semibold mb-2">Side effects</p>
                <p className="font-sm text-gray">{medicine?.sideEffect}</p>
                <ul className="font-sm text-gray ps-3 mt-2">
                  {medicine?.sideEffects?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              className={
                medicine
                  ? "group_btnOuter d-flex justify-content-end"
                  : "d-none"
              }
            >
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => setCurrentStep(2)}
              >
                Next Step
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
          <div className={currentStep === 2 ? "d-block" : "d-none"} id="step_2">
            <label className="d-block fw-semibold mb-2">
              Medicine Usages
              <span className="text-danger fw-bold ms-1">*</span>
            </label>
            <div className="d-flex flex-column gap-3" id="doseList">
              {medicineUsage.map((usage, index) => {
                return (
                  <div
                    className="border border-gray-400 rounded-8 p-4 bg-white position-relative"
                    key={index}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <span className="fw-bold fs-md">
                        Medicine {index + 1}
                      </span>
                      <Link
                        to="#"
                        className="d-flex align-items-center gap-1 fs-base fw-semibold text-danger"
                        onClick={() => deleteMedicineUsage(index)}
                      >
                        <span className="d-block material-symbols-outlined icon-lg">
                          delete
                        </span>
                      </Link>
                    </div>
                    <div className="form-group mb-4">
                      <div className="row">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                          <label
                            htmlFor={`timeToTake${index}`}
                            className="d-block fw-semibold mb-2"
                          >
                            Time to Take{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <input
                            type="time"
                            className="form-control px-4 py-3 lh-1 rounded-10 border-gray-100 shadow-none"
                            value={usage.time}
                            onChange={(e) =>
                              handleMedicineUsageChange(
                                index,
                                "time",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-lg-6">
                          <label
                            htmlFor={`dose${index}`}
                            className="d-block fw-semibold mb-2"
                          >
                            Dose <span className="text-danger fw-bold">*</span>
                          </label>
                          <input
                            type="number"
                            id={`dose${index}`}
                            className="form-control px-4 py-3 lh-1 rounded-10 border-gray-100 shadow-none"
                            placeholder="Dose in (mg)"
                            value={usage.dose}
                            onChange={(e) =>
                              handleMedicineUsageChange(
                                index,
                                "dose",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor={`direction${index}`}
                        className="d-block fw-semibold mb-3"
                      >
                        Direction <span className="text-danger fw-bold">*</span>
                      </label>
                      <div className="d-flex gap-4">
                        <div className="form-check mb-0 cursor-pointer">
                          <input
                            className="form-check-input p-0 shadow-none cursor-pointer"
                            type="radio"
                            name={`direction${index}`}
                            id={`afterTakingFood${index}`}
                            value="after"
                            checked={usage.direction === "after"}
                            onChange={() =>
                              handleMedicineUsageChange(
                                index,
                                "direction",
                                "after"
                              )
                            }
                          />
                          <label
                            className="form-check-label cursor-pointer"
                            htmlFor={`afterTakingFood${index}`}
                          >
                            After taking food
                          </label>
                        </div>
                        <div className="form-check mb-0 cursor-pointer">
                          <input
                            className="form-check-input p-0 shadow-none cursor-pointer"
                            type="radio"
                            name={`direction${index}`}
                            id={`beforeTakingFood${index}`}
                            value="before"
                            checked={usage.direction === "before"}
                            onChange={() =>
                              handleMedicineUsageChange(
                                index,
                                "direction",
                                "before"
                              )
                            }
                          />
                          <label
                            className="form-check-label cursor-pointer"
                            htmlFor={`beforeTakingFood${index}`}
                          >
                            Before taking food
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="group_btnOuter d-flex justify-content-between my-4">
              {" "}
              <button
                type="button"
                className="btn btn-outline-primary fw-semibold rounded-10 m-0 d-flex align-items-center gap-2"
                onClick={addNewMedicineUsage}
              >
                <span>Add Another Medicine</span>
                <span className="material-symbols-outlined">add</span>
              </button>
            </div>
            <div className="group_btnOuter d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-primary fw-semibold rounded-10 m-0 d-flex align-items-center gap-2"
                onClick={() => setCurrentStep(1)}
              >
                <span className="material-symbols-outlined">chevron_left</span>
                <span>Previous Step</span>
              </button>
              {/* Render Next button only if all medicineUsage have dose, time, direction */}
              {medicineUsage.length > 0 &&
                medicineUsage.every(
                  (usage) => usage.dose && usage.time && usage.direction
                ) && (
                  <button
                    type="button"
                    className="btn btn-primary fw-semibold rounded-10 m-0 d-flex align-items-center gap-2"
                    onClick={() => setCurrentStep(3)}
                  >
                    <span>Next Step</span>
                    <span className="material-symbols-outlined">
                      chevron_right
                    </span>
                  </button>
                )}
            </div>
          </div>
          <div className={currentStep === 3 ? "d-block" : "d-none"} id="step_3">
            <form onSubmit={(e) => e.preventDefault()}>
              {/* Day in the Week */}
              <div className="form-group mb-5">
                <label
                  htmlFor="daysInTheWeek"
                  className="d-block fw-semibold mb-2"
                >
                  Day in the Week <span className="text-danger fw-bold">*</span>
                </label>
                <ul className="days-in-week list-inline lh-1 text-center d-flex gap-3">
                  {weekDaysList.map((day) => (
                    <li className="flex-fill cursor-pointer" key={day.id}>
                      <input
                        type="checkbox"
                        className="d-none"
                        name="daysInTheWeek"
                        id={day.id}
                        checked={usageDaysOfweek.includes(day.value)}
                        onChange={() => handleDayToggle(day)}
                      />
                      <label
                        className={`d-block p-3 rounded-90 cursor-pointer${
                          usageDaysOfweek.includes(day.value)
                            ? " border border-primary bg-primary text-white"
                            : " border border-primary"
                        }`}
                        htmlFor={day.id}
                      >
                        {day.label}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Date Range */}
              <div className="form-group mb-4">
                <div className="row">
                  <div className="col-lg-6 mb-4 mb-lg-0">
                    <label
                      htmlFor="fromDate"
                      className="d-block fw-semibold mb-2"
                    >
                      From Date <span className="text-danger fw-bold">*</span>
                    </label>
                    <input
                      type="date"
                      id="fromDate"
                      className="form-control px-4 py-3 lh-1 rounded-10 border-gray-100 shadow-none"
                      placeholder="DD/MM/YYYY"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                      }}
                    />
                  </div>
                  <div className="col-lg-6">
                    <label
                      htmlFor="toDate"
                      className="d-block fw-semibold mb-2"
                    >
                      To Date <span className="text-danger fw-bold">*</span>
                    </label>
                    <input
                      type="date"
                      id="toDate"
                      className="form-control px-4 py-3 lh-1 rounded-10 border-gray-100 shadow-none"
                      placeholder="DD/MM/YYYY"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className="form-group mb-4">
                <label htmlFor="comments" className="d-block fw-semibold mb-2">
                  Comments
                </label>
                <textarea
                  name="comments"
                  id="comments"
                  rows="5"
                  placeholder="Enter Comments"
                  className="form-control px-4 py-3 lh-1 rounded-10 border-gray-100 shadow-none"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                ></textarea>
              </div>

              {/* Recommended By */}
              <div className="form-group mb-4">
                <label className="d-block fw-semibold mb-2">
                  Recommended By
                </label>
                <AsyncSelect
                  isClearable
                  name="referredBy"
                  loadOptions={loadUserOptions}
                  defaultOptions={false}
                  cacheOptions={false}
                  placeholder="Select Recommender"
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  value={referredBy}
                  onChange={setReferredBy}
                />
              </div>

              {/* Assign To */}
              <div className="form-group mb-4">
                <label className="d-block fw-semibold mb-2">Assign To</label>
                <AsyncSelect
                  isClearable
                  name="assignTo"
                  loadOptions={loadUserOptions}
                  defaultOptions={false}
                  cacheOptions={false}
                  placeholder="Select Assignee"
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  value={assignTo}
                  onChange={setAssignTo}
                />
              </div>
            </form>
            <div className="group_btnOuter d-flex justify-content-between pt-3 border-top mt-auto">
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={() => setCurrentStep(2)}
              >
                <span className="material-symbols-outlined">chevron_left</span>
                <span>Previous Step</span>
              </button>
              {/* Render Done button only if endDate >= startDate and at least one day selected */}
              {endDate >= startDate && usageDates.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveMedicineHandler}
                  disabled={isSaving}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        add_circle
                      </span>
                      Save Medication
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveMedicationPopup;
